import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './RecommendationExplanation.css';

/**
 * Beautiful component that explains WHY recommendations were made
 * Shows personalized reasoning based on user's profile data
 */
function RecommendationExplanation({ 
  healthProfile, 
  userPreferences, 
  pregnancyProfile,
  currentWeek,
  recommendationType = 'meal' // 'meal', 'nutrition', 'activity'
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!healthProfile && !userPreferences && !pregnancyProfile) {
    return null;
  }
  
  const explanations = generateExplanations(healthProfile, userPreferences, pregnancyProfile, currentWeek, recommendationType);
  
  if (explanations.length === 0) {
    return null;
  }
  
  return (
    <motion.div 
      className="recommendation-explanation"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div 
        className="explanation-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="explanation-icon">✨</div>
        <div className="explanation-title">
          <span className="title-text">Why These Recommendations?</span>
          <span className="title-subtitle">Personalized just for you</span>
        </div>
        <motion.div
          className="expand-icon"
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          ▼
        </motion.div>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="explanation-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="explanation-list">
              {explanations.map((explanation, index) => (
                <motion.div
                  key={index}
                  className="explanation-item"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="explanation-icon-small">{explanation.icon}</div>
                  <div className="explanation-text">
                    <div className="explanation-title-small">{explanation.title}</div>
                    <div className="explanation-description">{explanation.description}</div>
                    {explanation.source && (
                      <div className="explanation-source">Based on: {explanation.source}</div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function generateExplanations(healthProfile, userPreferences, pregnancyProfile, currentWeek, recommendationType) {
  const explanations = [];
  
  // Health Profile Explanations
  if (healthProfile) {
    // BMI-based
    if (healthProfile.bmi) {
      const bmiCategory = healthProfile.bmiCategory;
      if (bmiCategory === 'UNDERWEIGHT') {
        explanations.push({
          icon: '📊',
          title: 'Weight Considerations',
          description: `Your BMI indicates you're underweight. We've increased calorie recommendations to support healthy weight gain during pregnancy, following ACOG guidelines for optimal baby development.`,
          source: 'ACOG, WHO'
        });
      } else if (bmiCategory === 'OVERWEIGHT' || bmiCategory === 'OBESE') {
        explanations.push({
          icon: '📊',
          title: 'Weight Management',
          description: `Based on your BMI, we're providing recommendations for moderate, healthy weight gain during pregnancy. This helps reduce risks while ensuring your baby gets all necessary nutrients.`,
          source: 'ACOG, IOM'
        });
      }
    }
    
    // Calorie needs
    if (healthProfile.dailyCalorieNeeds) {
      const trimester = currentWeek <= 13 ? 1 : (currentWeek <= 27 ? 2 : 3);
      const adjustment = healthProfile.pregnancyAdjustment || 0;
      if (adjustment > 0) {
        explanations.push({
          icon: '🔥',
          title: 'Calorie Needs',
          description: `You need approximately ${Math.round(healthProfile.dailyCalorieNeeds)} calories per day. This includes ${Math.round(adjustment)} extra calories for your ${trimester === 1 ? 'first' : trimester === 2 ? 'second' : 'third'} trimester, as recommended by ACOG.`,
          source: 'ACOG'
        });
      }
    }
    
    // Medical conditions
    if (healthProfile.medicalConditionRequirements) {
      const reqs = healthProfile.medicalConditionRequirements;
      if (reqs.specialConsiderations && reqs.specialConsiderations.length > 0) {
        explanations.push({
          icon: '🏥',
          title: 'Medical Considerations',
          description: reqs.specialConsiderations.join(' '),
          source: 'ACOG, CDC, Mayo Clinic'
        });
      }
    }
  }
  
  // User Preferences Explanations
  if (userPreferences) {
    // Allergies
    if (userPreferences.allergies && userPreferences.allergies.length > 0) {
      explanations.push({
        icon: '🚫',
        title: 'Allergy Safety',
        description: `We've carefully filtered out all meals containing: ${userPreferences.allergies.join(', ')}. Your safety is our top priority.`,
        source: 'Food Safety Guidelines'
      });
    }
    
    // Food preferences
    if (userPreferences.foodPreferences && userPreferences.foodPreferences.length > 0) {
      explanations.push({
        icon: '❤️',
        title: 'Your Taste Preferences',
        description: `We've prioritized meals that match your preferences: ${userPreferences.foodPreferences.join(', ')}. Enjoy meals you'll love!`,
        source: 'Personalized Matching'
      });
    }
    
    // Favorite foods
    if (userPreferences.favoriteFoods) {
      explanations.push({
        icon: '⭐',
        title: 'Your Favorites',
        description: `We've included your favorite foods (${userPreferences.favoriteFoods}) in recommendations whenever possible, making your meals more enjoyable.`,
        source: 'Personal Preference'
      });
    }
    
    // Disliked foods
    if (userPreferences.dislikedFoods) {
      explanations.push({
        icon: '👎',
        title: 'Avoiding Dislikes',
        description: `We've excluded ${userPreferences.dislikedFoods} from your recommendations to ensure you enjoy every meal.`,
        source: 'Personal Preference'
      });
    }
  }
  
  // User Preferences - Pregnancy Experience (these are in userPreferences, not pregnancyProfile)
  if (userPreferences) {
    // Morning sickness
    if (userPreferences.morningSicknessSeverity && userPreferences.morningSicknessSeverity !== 'NONE') {
      const severity = userPreferences.morningSicknessSeverity.toLowerCase();
      explanations.push({
        icon: '🤢',
        title: 'Morning Sickness Support',
        description: `Since you're experiencing ${severity} morning sickness, we've selected gentle, easy-to-digest meals and included nausea-friendly options like ginger and bland foods.`,
        source: 'ACOG, Mayo Clinic'
      });
    }
    
    // Energy level
    if (userPreferences.energyLevel) {
      if (userPreferences.energyLevel === 'LOW') {
        explanations.push({
          icon: '😴',
          title: 'Energy-Boosting Meals',
          description: `We've included iron-rich and protein-packed meals to help combat fatigue and boost your energy levels naturally.`,
          source: 'Nutrition Science'
        });
      }
    }
    
    // Cooking time
    if (userPreferences.cookingTime) {
      const timeMap = {
        'QUICK': '15 minutes or less',
        'MODERATE': 'around 30 minutes',
        'FLEXIBLE': '1 hour or more'
      };
      explanations.push({
        icon: '⏱️',
        title: 'Quick & Easy Recipes',
        description: `All recommended meals can be prepared in ${timeMap[userPreferences.cookingTime] || 'your available time'}, fitting perfectly into your schedule.`,
        source: 'Time Management'
      });
    }
    
    // Cooking skill
    if (userPreferences.cookingSkill === 'BEGINNER') {
      explanations.push({
        icon: '👩‍🍳',
        title: 'Simple Recipes',
        description: `We've selected straightforward, beginner-friendly recipes with clear instructions, so you can prepare delicious meals with confidence.`,
        source: 'Cooking Guidance'
      });
    }
  }
  
  // Trimester-specific
  if (currentWeek) {
    const trimester = currentWeek <= 13 ? 1 : (currentWeek <= 27 ? 2 : 3);
    if (trimester === 1) {
      explanations.push({
        icon: '🌱',
        title: 'First Trimester Focus',
        description: `During your first trimester, we're emphasizing folate-rich foods and small, frequent meals to support early development and manage nausea.`,
        source: 'ACOG, CDC'
      });
    } else if (trimester === 2) {
      explanations.push({
        icon: '💪',
        title: 'Second Trimester Nutrition',
        description: `Your second trimester requires increased protein and calcium for rapid baby growth. We've included plenty of nutrient-dense options.`,
        source: 'ACOG, WHO'
      });
    } else {
      explanations.push({
        icon: '🎯',
        title: 'Third Trimester Needs',
        description: `In your final trimester, we're focusing on iron-rich foods and smaller, more frequent meals to support your baby's final growth spurt.`,
        source: 'ACOG, Mayo Clinic'
      });
    }
  }
  
  return explanations;
}

export default RecommendationExplanation;

